import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  bookingBulkApi,
  clientsApi,
  type BulkBatchSummary,
  type BulkSubmitResult,
  type BulkValidateResult,
  type Client,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout, PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { internalNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { formatDateTime, humanize } from '@/lib/format';
import {
  clearBulkIdempotency,
  clientScopeKey,
  readBulkIdempotency,
  resolveBulkIdempotencyKey,
} from '@/lib/bulkIdempotency';

type Step = 'upload' | 'review' | 'receipt';

function newIdempotencyKey() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function TemplateDownloadButtons({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const toast = useToast();
  const download = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      toast((err as { message?: string }).message ?? 'Could not download the file. Please try again.', 'error');
    }
  };
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
      <Button type="button" size={size} variant="secondary" onClick={() => void download(() => bookingBulkApi.downloadBookingsTemplate(false))}>
        Bookings template
      </Button>
      <Button type="button" size={size} variant="secondary" onClick={() => void download(() => bookingBulkApi.downloadBookingsTemplate(true))}>
        Bookings example
      </Button>
      <Button type="button" size={size} variant="secondary" onClick={() => void download(() => bookingBulkApi.downloadManifestsTemplate(false))}>
        Manifests template
      </Button>
      <Button type="button" size={size} variant="secondary" onClick={() => void download(() => bookingBulkApi.downloadManifestsTemplate(true))}>
        Manifests example
      </Button>
    </div>
  );
}

function BulkBookingWizard({
  isPortal,
  clientId,
  setClientId,
  clients,
}: {
  isPortal: boolean;
  clientId: string;
  setClientId: (id: string) => void;
  clients: Client[];
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState<Step>('upload');
  const [bookingsCsv, setBookingsCsv] = useState('');
  const [manifestsCsv, setManifestsCsv] = useState('');
  const [bookingsFilename, setBookingsFilename] = useState('');
  const [validation, setValidation] = useState<BulkValidateResult | null>(null);
  const [receipt, setReceipt] = useState<BulkSubmitResult | null>(null);
  const [error, setError] = useState('');
  /** True when the error is about the CSV contents, so template advice is relevant. */
  const [errorIsCsv, setErrorIsCsv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState<BulkBatchSummary[]>([]);
  const bookingsInputRef = useRef<HTMLInputElement>(null);
  const manifestsInputRef = useRef<HTMLInputElement>(null);
  /** Persisted across submit retries; regenerated only on new file upload or new sourceHash. */
  const idempotencyKeyRef = useRef<string | null>(null);
  const boundSourceHashRef = useRef<string | null>(null);

  const errorRows = useMemo(
    () => (validation?.rows ?? []).filter((r) => !r.ok),
    [validation],
  );

  const refreshBatches = async () => {
    try {
      const list = await bookingBulkApi.listBatches({
        clientId: isPortal ? undefined : clientId || undefined,
        limit: 20,
      });
      setBatches(list);
    } catch {
      // History is best-effort; don't block the wizard.
    }
  };

  const storage =
    typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  const scope = clientScopeKey({ isPortal, clientId });

  const clearIdempotency = () => {
    idempotencyKeyRef.current = null;
    boundSourceHashRef.current = null;
    clearBulkIdempotency(storage);
  };

  useEffect(() => {
    void refreshBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when client scope changes
  }, [clientId, isPortal]);

  // Drop stored key only when the client scope actually changes (not on first mount/reload).
  const prevScopeRef = useRef<string | null>(null);
  useEffect(() => {
    const next = clientScopeKey({ isPortal, clientId });
    if (prevScopeRef.current != null && prevScopeRef.current !== next) {
      clearIdempotency();
    }
    prevScopeRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional client-scope watch
  }, [clientId, isPortal]);

  // Restore idempotency binding after reload for the current client scope.
  useEffect(() => {
    const existing = readBulkIdempotency(storage);
    if (existing && existing.clientScope === scope) {
      idempotencyKeyRef.current = existing.idempotencyKey;
      boundSourceHashRef.current = existing.sourceHash;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time restore
  }, []);

  const readFile = async (file: File, kind: 'bookings' | 'manifests') => {
    const text = await file.text();
    if (kind === 'bookings') {
      setBookingsCsv(text);
      setBookingsFilename(file.name);
    } else {
      setManifestsCsv(text);
    }
    // Do NOT clear idempotency here. After reload the user must re-select the same
    // files; clearing on select would destroy the restored key before validate can
    // reuse it. Rotation happens in resolveBulkIdempotencyKey when the validated
    // sourceHash (bookings+manifests) or client scope differs.
    setValidation(null);
    setReceipt(null);
    setError('');
  };

  const runValidate = async () => {
    setError('');
    setErrorIsCsv(false);
    if (!isPortal && !clientId) {
      setError('Select a client');
      return;
    }
    if (!bookingsCsv.trim()) {
      setError('Upload a bookings CSV first');
      return;
    }
    setBusy(true);
    try {
      const result = await bookingBulkApi.validate({
        clientId: isPortal ? undefined : clientId,
        bookingsCsv,
        manifestsCsv: manifestsCsv.trim() || undefined,
      });
      setValidation(result);
      // Bind idempotency key to validated sourceHash; reuse across retries and reload.
      const key = resolveBulkIdempotencyKey({
        storage,
        clientScope: scope,
        sourceHash: result.sourceHash,
        newKey: newIdempotencyKey,
      });
      idempotencyKeyRef.current = key;
      boundSourceHashRef.current = result.sourceHash;
      setStep('review');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'We could not read this file');
      setErrorIsCsv(true);
    } finally {
      setBusy(false);
    }
  };

  const runSubmit = async () => {
    setError('');
    setErrorIsCsv(false);
    if (!validation?.ok) {
      setError('Fix validation errors before submitting');
      return;
    }
    const key = resolveBulkIdempotencyKey({
      storage,
      clientScope: scope,
      sourceHash: validation.sourceHash,
      newKey: newIdempotencyKey,
    });
    idempotencyKeyRef.current = key;
    boundSourceHashRef.current = validation.sourceHash;
    setBusy(true);
    try {
      const result = await bookingBulkApi.submit({
        clientId: isPortal ? undefined : clientId,
        bookingsCsv,
        manifestsCsv: manifestsCsv.trim() || undefined,
        idempotencyKey: key,
        sourceFilename: bookingsFilename || undefined,
      });
      setReceipt(result);
      setStep('receipt');
      toast(
        result.idempotentReplay
          ? 'This file was already submitted — showing the original result'
          : `${result.createdCount} trip${result.createdCount === 1 ? '' : 's'} booked`,
      );
      clearIdempotency();
      void refreshBatches();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const openBatchReceipt = async (batchId: string) => {
    setError('');
    setErrorIsCsv(false);
    setBusy(true);
    try {
      const detail = await bookingBulkApi.getBatch(batchId);
      setReceipt({
        batchId: detail.id,
        status: detail.status,
        createdCount: detail.createdCount,
        rejectedCount: detail.rejectedCount,
        rows: detail.rows,
        sourceHash: detail.sourceHash ?? undefined,
      });
      setStep('receipt');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Could not load that upload');
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = () => {
    if (!validation) return;
    const lines = ['row,customer_reference,field,message'];
    for (const row of errorRows) {
      for (const err of row.errors) {
        const cells = [row.row, row.customerReference, err.field ?? '', err.message].map((c) => {
          const s = String(c);
          const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
          return `"${safe.replace(/"/g, '""')}"`;
        });
        lines.push(cells.join(','));
      }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking-import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadResults = async (batchId: string) => {
    try {
      await bookingBulkApi.downloadBatchResults(batchId);
    } catch (err) {
      toast((err as { message?: string }).message ?? 'Could not download the results. Please try again.', 'error');
    }
  };

  const tripPath = (tripId: string) => (isPortal ? `/portal/trips/${tripId}` : `/app/dispatch/trips/${tripId}`);

  const resetWizard = () => {
    setStep('upload');
    setError('');
    setErrorIsCsv(false);
    setBookingsCsv('');
    setManifestsCsv('');
    setBookingsFilename('');
    setValidation(null);
    setReceipt(null);
    clearIdempotency();
    if (bookingsInputRef.current) bookingsInputRef.current.value = '';
    if (manifestsInputRef.current) manifestsInputRef.current.value = '';
  };

  return (
    <>
      <div className="page-header row-between">
        <div>
          <p className="kicker">Booking</p>
          <h1>Bulk CSV upload</h1>
          <p className="text-muted">
            Upload a CSV of bookings, check it, then submit. Either every row is booked or none are.
            Each booking has one pickup and one drop.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(isPortal ? '/portal/book' : '/app/dispatch/book')}
        >
          Single booking
        </Button>
      </div>

      <ol className="bulk-steps" aria-label="Bulk upload steps">
        {(['upload', 'review', 'receipt'] as Step[]).map((s, i) => (
          <li key={s} className={step === s ? 'active' : ''} aria-current={step === s ? 'step' : undefined}>
            {i + 1}.{' '}
            {s === 'upload' ? 'Upload' : s === 'review' ? 'Review' : 'Done'}
          </li>
        ))}
      </ol>

      {error && (
        <div className="panel" role="alert" style={{ marginBottom: '1rem', borderColor: 'var(--color-danger, #b33)' }}>
          <p className="form-error">{error}</p>
          {errorIsCsv && (
            <>
              <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
                Check your file matches the template columns, fix it, then upload it again.
              </p>
              <TemplateDownloadButtons size="sm" />
            </>
          )}
        </div>
      )}

      {step === 'upload' && (
        <section className="panel">
          <h2>1. Upload CSV</h2>
          <div className="bulk-template-row">
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
              Need a template? Start from the bookings template (or the filled-in example). The manifests file is
              optional: use it to list item-level cargo lines per booking. Use saved address labels where possible.
            </p>
            <TemplateDownloadButtons size="sm" />
          </div>
          {!isPortal && (
            <Select label="Client" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                </option>
              ))}
            </Select>
          )}
          <div className="import-grid" style={{ marginTop: '1rem' }}>
            <div>
              <label htmlFor="bulk-bookings-file" className="text-muted" style={{ fontSize: '0.8125rem', display: 'block' }}>
                Bookings CSV
              </label>
              <input
                id="bulk-bookings-file"
                ref={bookingsInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void readFile(file, 'bookings');
                }}
              />
              {bookingsFilename && (
                <p className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {bookingsFilename}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="bulk-manifests-file" className="text-muted" style={{ fontSize: '0.8125rem', display: 'block' }}>
                Manifests CSV (optional — item-level cargo lines per booking)
              </label>
              <input
                id="bulk-manifests-file"
                ref={manifestsInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void readFile(file, 'manifests');
                }}
              />
            </div>
          </div>
          <div className="row" style={{ gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button type="button" loading={busy} onClick={() => void runValidate()}>
              Check file
            </Button>
          </div>
        </section>
      )}

      {step === 'review' && validation && (
        <section className="panel">
          <h2>2. Review</h2>
          <p>
            {validation.validCount} of {validation.rowCount} row{validation.rowCount === 1 ? '' : 's'} ready
            {validation.errorCount ? ` · ${validation.errorCount} need fixing` : ''}
          </p>
          {validation.ok && (
            <div className="bulk-preview-table" style={{ marginBottom: '1rem' }}>
              <table>
                <thead>
                  <tr>
                    <th>Your reference</th>
                    <th>Date</th>
                    <th>Route</th>
                    <th>Vehicle type</th>
                    <th>Rate</th>
                    <th title="Item-level cargo lines from the optional manifests file">Cargo lines</th>
                  </tr>
                </thead>
                <tbody>
                  {validation.rows
                    .filter((r) => r.ok)
                    .map((r) => (
                      <tr key={r.row}>
                        <td>{r.customerReference}</td>
                        <td>{r.serviceDate ?? '—'}</td>
                        <td>{r.routeSummary ?? '—'}</td>
                        <td>{r.vehicleTypeCode ?? '—'}</td>
                        <td>{r.rateSummary ?? '—'}</td>
                        <td>{r.manifestLineCount ?? 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {validation.ok && (
            <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
              Cargo lines come from the optional manifests file (item-level cargo per booking); 0 means none were provided.
            </p>
          )}
          {!validation.ok && (
            <>
              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                Nothing has been booked yet. Fix the rows below in your file, then go back and upload it again.
              </p>
              <div className="row" style={{ gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <Button type="button" size="sm" variant="secondary" onClick={downloadErrors}>
                  Download list of problems (CSV)
                </Button>
                <TemplateDownloadButtons size="sm" />
              </div>
              <div className="bulk-error-table">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reference</th>
                      <th>Field</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorRows.flatMap((row) =>
                      row.errors.map((err, i) => (
                        <tr key={`${row.row}-${i}-${err.field}`}>
                          <td>{err.row}</td>
                          <td>{row.customerReference || '—'}</td>
                          <td>{err.field ?? '—'}</td>
                          <td>{err.message}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {validation.ok && (
            <p className="text-ok">
              All rows look good. Submit to book {validation.validCount} trip{validation.validCount === 1 ? '' : 's'}.
            </p>
          )}
          <div className="row" style={{ gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button type="button" variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <Button type="button" loading={busy} disabled={!validation.ok} onClick={() => void runSubmit()}>
              {validation.ok ? `Book ${validation.validCount} trip${validation.validCount === 1 ? '' : 's'}` : 'Submit'}
            </Button>
          </div>
        </section>
      )}

      {step === 'receipt' && receipt && (
        <section className="panel">
          <h2>3. Done</h2>
          {receipt.idempotentReplay && (
            <p className="text-muted">This file was already submitted — showing the original result.</p>
          )}
          <p>
            {receipt.createdCount} trip{receipt.createdCount === 1 ? '' : 's'} booked
            {receipt.rejectedCount ? ` · ${receipt.rejectedCount} not booked` : ''}
            {' · '}{humanize(receipt.status)}
          </p>
          <ul className="bulk-receipt-list">
            {receipt.rows
              .filter((r) => r.ok && r.tripId)
              .map((r) => (
                <li key={r.tripId}>
                  {r.customerReference} →{' '}
                  <Link to={tripPath(r.tripId!)}>{r.tripNumber ?? 'View trip'}</Link>
                </li>
              ))}
          </ul>
          <div className="row" style={{ gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <Button type="button" onClick={() => navigate(isPortal ? '/portal' : '/app/dispatch')}>
              {isPortal ? 'Go to my trips' : 'Go to dispatch board'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void downloadResults(receipt.batchId)}
            >
              Download results CSV
            </Button>
            <Button type="button" variant="secondary" onClick={resetWizard}>
              Upload another
            </Button>
          </div>
        </section>
      )}

      <section className="panel" style={{ marginTop: '1.25rem' }}>
        <h2>Previous uploads</h2>
        <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
          {isPortal ? 'Your earlier bulk uploads.' : 'Earlier bulk uploads for this client.'} Open one to see the trips it booked.
        </p>
        {batches.length === 0 ? (
          <p className="text-muted">No uploads yet.</p>
        ) : (
          <ul className="bulk-history-list">
            {batches.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => void openBatchReceipt(b.id)}
                  disabled={busy}
                >
                  {b.sourceFilename || `Upload of ${formatDateTime(b.createdAt)}`}
                </button>
                <span className="text-muted">
                  {' '}
                  · {humanize(b.status)} · {b.createdCount} of {b.rowCount} booked ·{' '}
                  {formatDateTime(b.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <style>{`
        .bulk-steps {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          list-style: none;
          padding: 0;
          margin: 0 0 1.25rem;
          font-size: 0.8125rem;
          color: var(--color-text-muted);
        }
        .bulk-steps .active {
          color: var(--color-charcoal);
          font-weight: 600;
        }
        .bulk-error-table,
        .bulk-preview-table {
          overflow: auto;
          max-height: 320px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
        }
        .bulk-error-table table,
        .bulk-preview-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8125rem;
        }
        .bulk-error-table th,
        .bulk-error-table td,
        .bulk-preview-table th,
        .bulk-preview-table td {
          text-align: left;
          padding: 0.4rem 0.6rem;
          border-bottom: 1px solid var(--color-border);
        }
        .bulk-receipt-list,
        .bulk-history-list {
          margin: 0.75rem 0 0;
          padding-left: 1.25rem;
          font-size: 0.875rem;
        }
        .bulk-history-list {
          list-style: none;
          padding-left: 0;
        }
        .bulk-history-list li {
          padding: 0.35rem 0;
          border-bottom: 1px solid var(--color-border);
        }
        .linkish {
          background: none;
          border: none;
          padding: 0;
          color: var(--color-accent, #0b5fff);
          cursor: pointer;
          text-decoration: underline;
          font: inherit;
        }
        .bulk-template-row {
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--color-border);
        }
        .import-grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }
      `}</style>
    </>
  );
}

export function BulkBookingUpload({ mode }: { mode: 'internal' | 'portal' }) {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const isPortal = mode === 'portal';
  const canBook = isPortal ? hasPermission('portal.book') : hasPermission('trips.book');

  const { data: clients } = useAsync(
    () => (isPortal ? Promise.resolve([] as Client[]) : clientsApi.list({ status: 'active' })),
    [isPortal],
  );

  const [clientId, setClientId] = useState(isPortal ? user?.clientId ?? '' : '');

  if (isPortal) {
    return (
      <PortalLayout>
        <div className="page">
          <div className="portal-nav">
            <PortalNav variant="top" />
          </div>
          {!canBook ? (
            <div className="page-header">
              <h1>Bulk upload</h1>
              <p className="text-muted">Your role cannot book trips.</p>
              <Button type="button" variant="secondary" onClick={() => navigate('/portal')}>
                Back
              </Button>
            </div>
          ) : (
            <BulkBookingWizard
              isPortal
              clientId={clientId}
              setClientId={setClientId}
              clients={[]}
            />
          )}
        </div>
      </PortalLayout>
    );
  }

  return (
    <Layout navItems={internalNavItems}>
      <div className="page">
        {!canBook ? (
          <div className="page-header">
            <h1>Bulk upload</h1>
            <p className="text-muted">You do not have permission to book trips.</p>
          </div>
        ) : (
          <BulkBookingWizard
            isPortal={false}
            clientId={clientId}
            setClientId={setClientId}
            clients={clients ?? []}
          />
        )}
      </div>
    </Layout>
  );
}
